import { Injectable, Injector } from "@angular/core";
import { FormDialogTemplate } from "../../../../shared/form-dialog/form-dialog.template";
import { FormBuilder, FormGroup, Validators, FormControl } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { RequestService } from "../request.service";
import { ComponentCategories } from "./component-categories.template";
import { CtwrComponentCategoryService } from "../../ctwr.service";
import { ComponentDetail } from "./component-detail.template";

const notificationText = '*You must select at least 1 item. **You must select at least one item for each PTE when Simple Request is unchecked.';

@Injectable()
export class ToolingComponent {
    formDialog: FormDialogTemplate;
    editForm: FormGroup;
    editDataItem: any;
    dialogTitle: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
    componentCategoryTemplate: ComponentCategories = new ComponentCategories(this.injector);
    partDetail: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    isSimpleRequest = true;
    private readonly formBuilder: FormBuilder;
    private readonly requestService: RequestService;
    private readonly componentCategoryService: CtwrComponentCategoryService;

    constructor(
        private readonly injector: Injector
    ) {
        this.formBuilder = injector.get(FormBuilder);
        this.requestService = injector.get(RequestService);
        this.componentCategoryService = injector.get(CtwrComponentCategoryService);

        this.editForm = this.formBuilder.group({
            PartNumber: [],
            ProcessName: [],
            ComponentCategories: [],
            valid: [false, Validators.requiredTrue]
        })

        this.formDialog = new FormDialogTemplate();
        this.formDialog.largeForm = true;
        this.formDialog.setHeight(540);
        this.formDialog.save = this.onSave;
        this.formDialog.cancel = this.closeDialog;
        this.formDialog.notificationLabel = { flag: 'info', value: notificationText };


        this.formDialog.initilize(this.dialogTitle, this.editForm, this.formDialogTabs);

        this.dialogTitle.next('Tooling Components');

        const detailTemplate: ComponentDetail = this.componentCategoryTemplate.detailTemplate
        detailTemplate.isEditing
            .subscribe(x => this.validateForm(x));

    }

    setPartDetail(part: any, isResetSave = true, isPTE1: boolean = undefined): Promise<any> {
        this.partDetail.next(part);
        if (isResetSave) this.formDialog.save = this.onSave;
        let response: Promise<any> = Promise.resolve([]);
        if (part) {
            this.updateComponentCategoryTemplate([]);
            if (part.RequestId) {
                const _partId = part.PartId ? part.PartId : part.Id;
                let _path = `/ToolingComponentListByRequestPart/${part.ProcessId}/${part.RequestId}/${_partId}`;
                const addPTE1 = (isPTE1 !== undefined) ? `?IsPTE1=${isPTE1}` : '';
                _path += addPTE1;
                response = this.componentCategoryService.read(_path)
                    .then(x => this.updateComponentCategoryTemplate(x));

                let _partNumber = this.editForm.get('PartNumber');
                const _processName = this.editForm.get('ProcessName');
                if (!_partNumber) {
                    this.editForm.addControl('PartNumber', new FormControl());
                    _partNumber = this.editForm.get('PartNumber');
                }

                _partNumber.setValue(part.PartNumber)
                _processName.setValue(part.ProcessName)

            } else {
                response = this.componentCategoryService.read(`/ToolingComponentListByProcess/${part.ProcessId}`)
                    .then(x => this.updateComponentCategoryTemplate(x));
            }
        }
        return response;
    }


    private updateComponentCategoryTemplate(categories: any[]) {
        this.componentCategoryTemplate.setToolingComponentList(categories);
        this.updateComponentCategories(categories);
        this.validateForm(false);
    }


    /**
     * 
     * @param isEditing 
     */
    validateForm(isEditing: boolean) {
        const _data = this.componentCategoryService.value;
        let valid: boolean;

        if (_data && _data.length > 0 && _data[0].RequestComponents) {
            valid = this.isItemSelected(_data);
        }

        this.requestService.isToolingFormValid.next(valid);
        this.editForm.get('valid').setValue(!isEditing ? valid : false);
    }

    /**
     * 
     * @param data 
     */
    private isItemSelected(data: any[]): boolean {
        let response: boolean;
        if (this.isSimpleRequest) {
            response = data.some(cc => cc.RequestComponents
                .some(rc => rc.RequestItems
                    .some(ri => ri.IsSelected)
                )
            );
        } else {
            const hasPTE1 = data.some(cc => cc.componentCategoryView.PTE === 'PTE1');
            const hasPTE2 = data.some(cc => cc.componentCategoryView.PTE === 'PTE2');
            const isPTE1ItemSelected = hasPTE1 && data.some(cc => {
                return cc.componentCategoryView.PTE === 'PTE1' &&
                    cc.RequestComponents
                        .some(rc => rc.RequestItems
                            .some(ri => ri.IsSelected)
                        );
            });
            const isPTE2ItemSelected = hasPTE2 && data.some(cc => {
                return cc.componentCategoryView.PTE === 'PTE2' &&
                    cc.RequestComponents
                        .some(rc => rc.RequestItems
                            .some(ri => ri.IsSelected)
                        );
            });

            response = (!hasPTE1 || hasPTE1 && isPTE1ItemSelected) &&
                (!hasPTE2 || hasPTE2 && isPTE2ItemSelected);
        }
        return response;
    }


    updateComponentCategories = (dataItem: any[]) => {
        const ctrl = this.editForm.get('ComponentCategories');
        ctrl.setValue(dataItem);
    }


    closeForm() {
        this.formDialog.editForm = this.editForm;
    }


    open = (isOpenRequestServiceDialog = true) => {
        this.formDialog.next(this.partDetail.value);
        this.validateForm(false);
        if (isOpenRequestServiceDialog) this.requestService.openDialog(this.formDialog);
    }


    onSave = ({ dataItem, isNew }) => {
        const ccData = this.getFormData();
        this.updateComponentCategories(ccData);
        this.requestService.closeDialog();
    }


    getFormData(): any {
        const obj = this.componentCategoryTemplate.editForm.value;
        obj.ComponentCategories.forEach(cc => cc.RequestComponents
            .map(c => {
                c.RequestItems = c.RequestItems.map(i => {
                    delete i.Id;
                    delete i.Name;
                    return i;
                });
                delete c.Id;
                delete c.Name;
                delete c.ItemIds;
                delete c.ItemNames;
                return c;
            }));
        return obj.ComponentCategories;
    }


    closeDialog = () => {
        this.isSimpleRequest = true;
        this.requestService.closeDialog();
    }


    formControls = [
        {
            type: 'value',
            title: 'Part Number',
            name: 'PartNumber',
            inline: true
        },
        {
            type: 'value',
            title: 'Process',
            name: 'ProcessName',
            inline: true
        },
        {
            type: 'data-grid',
            name: 'ComponentCategories',
            stretch: true,
            template: this.componentCategoryTemplate
        }
    ]

    formDialogTabs = [
        {
            title: "Tooling Components",
            selected: true,
            formControls: this.formControls,
        },

    ]

}

